import React, { useState } from 'react'
import { format } from 'date-fns'
import {
  CalendarIcon,
  DollarSign,
  Edit,
  Trash2,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ReceiptText,
  Loader2,
} from 'lucide-react'
import { toast } from 'react-hot-toast'
import { cn, formatHours, istDate } from '@/lib/utils'
import {
  useGetProjectDetailsQuery,
  useDeleteProjectDetailsMutation,
  type ProjectDetails,
} from '@/redux/service/project'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog'
import { Button } from './ui/button'
import { Calendar } from './ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table'
import { CustomPagination } from './CustomPagination'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip'
import { Skeleton } from './ui/skeleton'
import { useCheckPermission } from '@/hooks/useCheckPermission'

interface ProjectDetailsListProps {
  projectId: number
  showActions?: boolean
  onEdit?: (detail: ProjectDetails) => void
  headerActions?: React.ReactNode
}

const ProjectDetailsList: React.FC<ProjectDetailsListProps> = ({
  projectId,
  showActions = false,
  onEdit,
  headerActions,
}) => {
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [detailToDelete, setDetailToDelete] = useState<ProjectDetails | null>(
    null
  )
  const [deleteProjectDetails, { isLoading: isDeleting }] =
    useDeleteProjectDetailsMutation()
  const [page, setPage] = useState<number>(1)
  const [limit, setLimit] = useState<number>(10)
  const [startDate, setStartDate] = useState<Date | undefined>(undefined)
  const [endDate, setEndDate] = useState<Date | undefined>(undefined)
  const { hasPageAccess } = useCheckPermission()

  const [sortBy, setSortBy] = useState<string | undefined>()
  const [order, setOrder] = useState<'ASC' | 'DESC' | undefined>()

  const handleSort = (column: string) => {
    if (sortBy === column) {
      if (order === 'ASC') {
        setOrder('DESC')
      } else if (order === 'DESC') {
        setSortBy(undefined)
        setOrder(undefined)
      }
    } else {
      setSortBy(column)
      setOrder('ASC')
    }
    setPage(1)
  }

  const getSortIcon = (column: string) => {
    if (sortBy !== column)
      return <ArrowUpDown className='w-3 h-3 ml-1 opacity-40 inline-block' />
    if (order === 'ASC')
      return <ArrowUp className='w-3 h-3 ml-1 inline-block text-primary' />
    return <ArrowDown className='w-3 h-3 ml-1 inline-block text-primary' />
  }

  const {
    data: projectDetails,
    isLoading: detailsLoading,
    isFetching: isdetailsFetching,
    isError,
  } = useGetProjectDetailsQuery(
    {
      projectId,
      page,
      limit,
      ...(startDate && { start_date: format(startDate, 'yyyy-MM-dd') }),
      ...(endDate && { end_date: format(endDate, 'yyyy-MM-dd') }),
      sort_by: sortBy,
      order: order,
    },
    { skip: !projectId }
  )

  const hasActiveFilters = startDate || endDate || sortBy

  return (
    <div className='space-y-4'>
      {/* ── Header ── */}
      <div className='flex flex-col sm:flex-row sm:items-center justify-between gap-3'>
        <div className='flex items-center gap-3'>
          <div className='flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary/10 border border-primary/20'>
            <ReceiptText className='w-4 h-4 text-primary' />
            <span className='text-sm font-semibold text-primary'>
              Project Details
            </span>
          </div>
          {headerActions}
        </div>

        {/* ── Date filters ── */}
        <div className='flex flex-wrap items-center gap-2'>
          {/* Start Date */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant='outline'
                size='sm'
                className={cn(
                  'h-8 w-[130px] justify-start text-left font-normal text-xs border-border hover:bg-accent/30 transition-colors rounded-lg',
                  !startDate && 'text-muted-foreground'
                )}
              >
                <CalendarIcon className='mr-1.5 h-3 w-3 shrink-0' />
                {startDate ? format(startDate, 'PP') : 'Start Date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className='w-auto p-0' align='end'>
              <Calendar
                mode='single'
                captionLayout='dropdown'
                fromYear={2018}
                selected={startDate}
                onSelect={date => {
                  setStartDate(date)
                  setPage(1)
                }}
              />
            </PopoverContent>
          </Popover>

          {/* End Date */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant='outline'
                size='sm'
                className={cn(
                  'h-8 w-[130px] justify-start text-left font-normal text-xs border-border hover:bg-accent/30 transition-colors rounded-lg',
                  !endDate && 'text-muted-foreground'
                )}
              >
                <CalendarIcon className='mr-1.5 h-3 w-3 shrink-0' />
                {endDate ? format(endDate, 'PP') : 'End Date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className='w-auto p-0' align='end'>
              <Calendar
                mode='single'
                captionLayout='dropdown'
                fromYear={2018}
                selected={endDate}
                onSelect={date => {
                  setEndDate(date)
                  setPage(1)
                }}
                initialFocus
                disabled={date => (startDate ? date < startDate : false)}
              />
            </PopoverContent>
          </Popover>

          {/* Clear */}
          {hasActiveFilters && (
            <Button
              variant='ghost'
              size='sm'
              className='h-8 px-2 text-xs text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors flex items-center gap-1'
              onClick={() => {
                setStartDate(undefined)
                setEndDate(undefined)
                setSortBy(undefined)
                setOrder(undefined)
                setPage(1)
              }}
            >
              <X className='h-3 w-3' />
              Reset
            </Button>
          )}
        </div>
      </div>

      {/* ── Table card ── */}
      <div className='border border-border rounded-sm bg-card shadow-sm overflow-hidden'>
        <div className='overflow-x-auto'>
          <Table className='border-none'>
            <TableHeader>
              <TableRow className='border-b border-border bg-muted/40 hover:bg-muted/40'>
                {(hasPageAccess('finance') ||
                  hasPageAccess('mini_finance')) && (
                  <TableHead
                    className='text-xs font-semibold uppercase tracking-wide text-muted-foreground cursor-pointer select-none whitespace-nowrap px-4'
                    onClick={() => handleSort('total_budget')}
                  >
                    <div className='flex items-center gap-0.5'>
                      Budget {getSortIcon('total_budget')}
                    </div>
                  </TableHead>
                )}
                <TableHead
                  className='text-xs font-semibold uppercase tracking-wide text-muted-foreground cursor-pointer select-none whitespace-nowrap px-4'
                  onClick={() => handleSort('total_hours')}
                >
                  <div className='flex items-center gap-0.5'>
                    Hours {getSortIcon('total_hours')}
                  </div>
                </TableHead>
                {(hasPageAccess('finance') ||
                  hasPageAccess('mini_finance')) && (
                  <TableHead
                    className='text-xs font-semibold uppercase tracking-wide text-muted-foreground cursor-pointer select-none whitespace-nowrap px-4'
                    onClick={() => handleSort('hourly_cost')}
                  >
                    <div className='flex items-center gap-0.5'>
                      Hourly Rate {getSortIcon('hourly_cost')}
                    </div>
                  </TableHead>
                )}
                <TableHead className='text-xs font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap px-4'>
                  Milestone
                </TableHead>
                <TableHead
                  className='text-xs font-semibold uppercase tracking-wide text-muted-foreground cursor-pointer select-none whitespace-nowrap px-4'
                  onClick={() => handleSort('description')}
                >
                  <div className='flex items-center gap-0.5'>
                    Description {getSortIcon('description')}
                  </div>
                </TableHead>

                <TableHead
                  className='text-xs font-semibold uppercase tracking-wide text-muted-foreground cursor-pointer select-none whitespace-nowrap px-4'
                  onClick={() => handleSort('start_date')}
                >
                  <div className='flex items-center gap-0.5'>
                    Start Date {getSortIcon('start_date')}
                  </div>
                </TableHead>
                <TableHead
                  className='text-xs font-semibold uppercase tracking-wide text-muted-foreground cursor-pointer select-none whitespace-nowrap px-4'
                  onClick={() => handleSort('end_date')}
                >
                  <div className='flex items-center gap-0.5'>
                    End Date {getSortIcon('end_date')}
                  </div>
                </TableHead>
                <TableHead
                  className='text-xs font-semibold uppercase tracking-wide text-muted-foreground cursor-pointer select-none whitespace-nowrap px-4'
                  onClick={() => handleSort('created_at')}
                >
                  <div className='flex items-center gap-0.5'>
                    Created {getSortIcon('created_at')}
                  </div>
                </TableHead>
                {(showActions ||
                  hasPageAccess('finance') ||
                  hasPageAccess('mini_finance')) && (
                  <TableHead className='text-xs font-semibold uppercase tracking-wide text-muted-foreground text-right whitespace-nowrap px-4'>
                    Actions
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>

            <TableBody>
              {detailsLoading || isdetailsFetching ? (
                [...Array(limit)].map((_, i) => (
                  <TableRow key={i} className='border-b border-border/50'>
                    {[...Array(showActions ? 9 : 8)].map((_, j) => (
                      <TableCell key={j} className='px-4 py-3'>
                        <Skeleton className='h-4 w-full rounded-md' />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : !isError &&
                projectDetails?.data &&
                projectDetails.data.length > 0 ? (
                projectDetails.data.map(d => (
                  <TableRow
                    key={d?.id}
                    className='border-b border-border/50 hover:bg-accent/30 transition-colors duration-150 group'
                  >
                    {(hasPageAccess('finance') ||
                      hasPageAccess('mini_finance')) && (
                      <TableCell className='px-4 py-3'>
                        <span className='inline-flex items-center gap-1 text-sm font-semibold text-green-600 dark:text-green-400'>
                          <DollarSign className='w-3.5 h-3.5' />
                          {Number(d?.total_budget ?? 0).toFixed(2)}
                        </span>
                      </TableCell>
                    )}

                    <TableCell className='px-4 py-3 text-sm font-medium'>
                      {formatHours(d?.total_hours)}
                    </TableCell>
                    {(hasPageAccess('finance') ||
                      hasPageAccess('mini_finance')) && (
                      <TableCell className='px-4 py-3 text-sm font-medium'>
                        ${Number(d?.hourly_cost ?? 0).toFixed(2)}
                      </TableCell>
                    )}
                    {/* Milestones */}
                    <TableCell className='px-4 py-3 max-w-[280px]'>
                      {(() => {
                        const validMilestones =
                          d?.milestones?.filter(
                            (m): m is NonNullable<typeof m> => m != null
                          ) ?? []
                        if (validMilestones.length === 0)
                          return (
                            <span className='text-muted-foreground text-sm'>
                              —
                            </span>
                          )
                        const MAX_VISIBLE = 2
                        const visible = validMilestones.slice(0, MAX_VISIBLE)
                        const overflow = validMilestones.slice(MAX_VISIBLE)
                        return (
                          <div className='flex flex-wrap gap-1 items-center'>
                            {visible.map((m, idx) => (
                              <span
                                key={`${m?.name}-${idx}`}
                                className='inline-flex items-center px-2 py-0.5 rounded-md bg-primary/10 text-primary border border-primary/20 text-xs font-medium max-w-[200px] truncate'
                                title={m?.name ?? ''}
                              >
                                {m?.name ?? '—'}
                              </span>
                            ))}
                            {overflow.length > 0 && (
                              <TooltipProvider delayDuration={100}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      type='button'
                                      className='inline-flex items-center px-2 py-0.5 rounded-md border border-border bg-muted hover:bg-accent text-xs font-semibold text-muted-foreground transition-colors'
                                    >
                                      +{overflow.length}
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent
                                    side='top'
                                    align='start'
                                    className='max-w-xs p-2'
                                  >
                                    <ul className='flex flex-col gap-1'>
                                      {validMilestones.map((m, idx) => (
                                        <li
                                          key={`tt-${m?.name}-${idx}`}
                                          className='flex items-start gap-1.5 text-xs leading-snug'
                                        >
                                          <span className='mt-1.5 inline-block h-1.5 w-1.5 rounded-full bg-primary shrink-0' />
                                          <span>{m?.name ?? '—'}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                        )
                      })()}
                    </TableCell>

                    {/* Description */}
                    <TableCell
                      className='px-4 py-3 max-w-[280px] truncate text-sm text-muted-foreground'
                      title={d?.description ?? ''}
                    >
                      {d.description ?? '—'}
                    </TableCell>

                    <TableCell className='px-4 py-3 text-sm whitespace-nowrap'>
                      {istDate(d?.start_date)}
                    </TableCell>
                    <TableCell className='px-4 py-3 text-sm whitespace-nowrap'>
                      {istDate(d?.end_date)}
                    </TableCell>
                    <TableCell className='px-4 py-3 text-xs text-muted-foreground whitespace-nowrap'>
                      {istDate(d?.created_at)}
                    </TableCell>

                    {(showActions ||
                      hasPageAccess('finance') ||
                      hasPageAccess('mini_finance')) && (
                      <TableCell className='sticky right-0 z-10 border-s bg-background/95 backdrop-blur-sm flex items-center gap-2 shadow-[-8px_0_12px_-12px_rgba(0,0,0,0.35)] opacity-0 group-hover:opacity-100 transition-opacity duration-300'>
                        <Button
                          size='icon'
                          variant='ghost'
                          onClick={() => onEdit?.(d)}
                          className='h-5 w-5 hover:bg-accent hover:text-accent-foreground'
                        >
                          <Edit />
                        </Button>
                        <Button
                          size='icon'
                          variant='ghost'
                          onClick={() => {
                            setDetailToDelete(d)
                            setDeleteConfirmOpen(true)
                          }}
                          className='h-5 w-5 text-destructive/70 hover:text-destructive hover:bg-destructive/10'
                        >
                          <Trash2 />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={showActions ? 9 : 8}
                    className='h-32 text-center'
                  >
                    <div className='flex flex-col items-center justify-center gap-1.5'>
                      <ReceiptText className='w-8 h-8 text-muted-foreground/40' />
                      <p className='text-sm font-medium text-muted-foreground'>
                        No project details found
                      </p>
                      <p className='text-xs text-muted-foreground/60'>
                        Try adjusting the date range or clearing filters
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* ── Pagination ── */}
      {projectDetails?.data && projectDetails.data.length > 1 && (
        <div className='pt-1'>
          <CustomPagination
            page={page}
            limit={limit}
            setPage={setPage}
            setLimit={setLimit}
            totalPages={projectDetails?.pagination?.totalPages}
            totalRecords={projectDetails?.pagination?.totalRecords}
          />
        </div>
      )}
      {/* ── Delete Confirmation Dialog ── */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className='max-w-sm p-0 gap-0'>
          <DialogHeader className='px-6 pt-6 pb-4 border-b border-border'>
            <div className='flex items-center gap-3'>
              <div className='flex items-center justify-center w-8 h-8 rounded-lg bg-destructive/10 border border-destructive/20 shrink-0'>
                <Trash2 className='w-4 h-4 text-destructive' />
              </div>
              <DialogTitle className='text-base font-semibold'>
                Delete Project Detail
              </DialogTitle>
            </div>
          </DialogHeader>
          <div className='px-6 py-5'>
            <p className='text-sm text-muted-foreground'>
              Are you sure you want to delete this project detail? This action
              cannot be undone.
            </p>
          </div>
          <div className='flex justify-end gap-2 px-6 pb-5'>
            <Button
              variant='outline'
              size='sm'
              className='h-8 text-xs border-border hover:bg-accent/30'
              onClick={() => setDeleteConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant='destructive'
              size='sm'
              className='h-8 text-xs'
              disabled={isDeleting}
              onClick={async () => {
                if (detailToDelete?.id && projectId) {
                  try {
                    await deleteProjectDetails({
                      project_id: projectId,
                      id: detailToDelete.id,
                    }).unwrap()
                    toast.success('Project detail deleted successfully')
                    setDeleteConfirmOpen(false)
                    setDetailToDelete(null)
                  } catch {
                    toast.error('Failed to delete project detail')
                  }
                }
              }}
            >
              {isDeleting ? (
                <>
                  <Loader2 className='w-3 h-3 mr-1.5 animate-spin' />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default ProjectDetailsList
